import { useState } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary';
import { TradingChart } from './components/TradingChart';
import { OrderBook } from './components/OrderBook';
import { AccountBalances } from './components/AccountBalances';
import { PnLDashboard } from './components/PnLDashboard';
import { StrategyConfig } from './components/StrategyConfig';
import { OrderManagement } from './components/OrderManagement';
import { SpreadMonitor } from './components/SpreadMonitor';
import { PositionTracker } from './components/PositionTracker';
import { ConditionalOrders } from './components/ConditionalOrders';
import { AlertManagement } from './components/AlertManagement';
import { Tokenomics } from './components/Tokenomics';
import { WhaleAnalysis } from './components/WhaleAnalysis';
import { GovernanceMetrics } from './components/GovernanceMetrics';
import { DexMonitor } from './components/DexMonitor';
import { DeveloperActivity } from './components/DeveloperActivity';
import { LiquidityDepth } from './components/LiquidityDepth';
import { SpreadHistory } from './components/SpreadHistory';
import { PegStatus } from './components/PegStatus';
import { SystemOverview } from './components/SystemOverview';
import './App.css';

type TabType = 'dashboard' | 'orders' | 'positions' | 'strategies' | 'alerts' | 'tokenomics' | 'depth' | 'dex' | 'analysis';

function App() {
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');

  return (
    <div className="app">
      <header className="app-header">
        <div className="logo">
          <h1>TRADE</h1>
          <span className="subtitle">FCO/USDT Trading Dashboard</span>
        </div>
        <nav className="main-nav">
          <button className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => setActiveTab('dashboard')}>Dashboard</button>
          <button className={activeTab === 'orders' ? 'active' : ''} onClick={() => setActiveTab('orders')}>Orders</button>
          <button className={activeTab === 'positions' ? 'active' : ''} onClick={() => setActiveTab('positions')}>Positions</button>
          <button className={activeTab === 'strategies' ? 'active' : ''} onClick={() => setActiveTab('strategies')}>Strategies</button>
          <button className={activeTab === 'alerts' ? 'active' : ''} onClick={() => setActiveTab('alerts')}>Alerts</button>
          <button className={activeTab === 'tokenomics' ? 'active' : ''} onClick={() => setActiveTab('tokenomics')}>Tokenomics</button>
          <button className={activeTab === 'depth' ? 'active' : ''} onClick={() => setActiveTab('depth')}>Depth</button>
          <button className={activeTab === 'dex' ? 'active' : ''} onClick={() => setActiveTab('dex')}>DEX</button>
          <button className={activeTab === 'analysis' ? 'active' : ''} onClick={() => setActiveTab('analysis')}>Analysis</button>
        </nav>
      </header>

      <main className="app-main">
        {activeTab === 'dashboard' && (
          <div className="dashboard-layout">
            <div className="system-section">
              <ErrorBoundary name="SystemOverview"><SystemOverview /></ErrorBoundary>
            </div>
            <div className="top-section">
              <ErrorBoundary name="SpreadMonitor"><SpreadMonitor /></ErrorBoundary>
            </div>
            <div className="main-content">
              <div className="chart-section">
                <ErrorBoundary name="TradingChart"><TradingChart height={400} showVolume={true} /></ErrorBoundary>
              </div>
              <div className="sidebar">
                <ErrorBoundary name="OrderBook"><OrderBook depth={12} /></ErrorBoundary>
              </div>
            </div>
            <div className="monitoring-section">
              <div className="spread-history-panel">
                <ErrorBoundary name="SpreadHistory"><SpreadHistory hours={24} /></ErrorBoundary>
              </div>
              <div className="peg-status-panel">
                <ErrorBoundary name="PegStatus"><PegStatus hours={24} /></ErrorBoundary>
              </div>
            </div>
            <div className="bottom-section">
              <div className="balances-panel">
                <ErrorBoundary name="AccountBalances"><AccountBalances /></ErrorBoundary>
              </div>
              <div className="pnl-panel">
                <ErrorBoundary name="PnLDashboard"><PnLDashboard /></ErrorBoundary>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="orders-layout">
            <div className="orders-panels">
              <div className="regular-orders-panel">
                <ErrorBoundary name="OrderManagement"><OrderManagement /></ErrorBoundary>
              </div>
              <div className="conditional-orders-panel">
                <ErrorBoundary name="ConditionalOrders"><ConditionalOrders /></ErrorBoundary>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'positions' && (
          <div className="positions-layout">
            <ErrorBoundary name="PositionTracker"><PositionTracker /></ErrorBoundary>
          </div>
        )}

        {activeTab === 'strategies' && (
          <div className="strategies-layout">
            <ErrorBoundary name="StrategyConfig"><StrategyConfig /></ErrorBoundary>
          </div>
        )}

        {activeTab === 'alerts' && (
          <div className="alerts-layout">
            <ErrorBoundary name="AlertManagement"><AlertManagement /></ErrorBoundary>
          </div>
        )}

        {activeTab === 'tokenomics' && (
          <div className="tokenomics-layout">
            <ErrorBoundary name="Tokenomics"><Tokenomics /></ErrorBoundary>
            <ErrorBoundary name="WhaleAnalysis"><WhaleAnalysis /></ErrorBoundary>
            <ErrorBoundary name="GovernanceMetrics"><GovernanceMetrics /></ErrorBoundary>
            <ErrorBoundary name="DeveloperActivity"><DeveloperActivity /></ErrorBoundary>
          </div>
        )}

        {activeTab === 'depth' && (
          <div className="depth-layout">
            <ErrorBoundary name="LiquidityDepth"><LiquidityDepth /></ErrorBoundary>
          </div>
        )}

        {activeTab === 'dex' && (
          <div className="dex-layout">
            <ErrorBoundary name="DexMonitor"><DexMonitor /></ErrorBoundary>
          </div>
        )}

        {activeTab === 'analysis' && (
          <div className="analysis-layout">
            <div className="chart-full">
              <ErrorBoundary name="TradingChart"><TradingChart height={500} showVolume={true} /></ErrorBoundary>
            </div>
            <div className="analysis-panels">
              <div className="indicators-panel">
                <h3>Technical Indicators</h3>
                <p>RSI, MACD, Bollinger Bands data will appear here</p>
              </div>
              <div className="levels-panel">
                <h3>Support/Resistance</h3>
                <p>Key price levels will appear here</p>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="app-footer">
        <span>TRADE v2.1 - FCO/USDT Market Making</span>
        <span className="connection-status connected">Connected</span>
      </footer>
    </div>
  );
}

export default App;
